/**
 * Schema.org / JSON-LD payloads for BlueLock Freights.
 *
 * Public, accurate, non-PII info only. Compensation language is intentionally
 * conservative — no implied guarantees, no per-mile rates promised, "no income
 * is guaranteed" language present where relevant.
 */

const ORG_NAME = 'BlueLock Freights';
const LEGAL_NAME = 'BlueLock Transport LLC';
const WEBSITE = 'https://bluelockfreights.com';
const PORTAL = 'https://portal.bluelockfreights.com';

const ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '4370 Chicago Drive SW',
  addressLocality: 'Grandville',
  addressRegion: 'MI',
  postalCode: '49418',
  addressCountry: 'US',
};

export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: ORG_NAME,
  legalName: LEGAL_NAME,
  url: WEBSITE,
  description:
    'BlueLock Freights operates as a trade/brand name associated with BlueLock Transport LLC, a Michigan-based motor carrier. We support Owner-Operators, Lease-On Drivers, and Company Drivers under USDOT 4557330 / MC 1810883.',
  address: ADDRESS,
  contactPoint: [
    {
      '@type': 'ContactPoint',
      contactType: 'recruiting',
      email: 'recruiting@bluelockfreights.com',
      telephone: '+1-616-438-6969',
      areaServed: 'US',
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'dispatch',
      email: 'dispatch@bluelockfreights.com',
      telephone: '+1-616-292-9452',
      areaServed: 'US',
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'compliance',
      email: 'compliance@bluelockfreights.com',
      areaServed: 'US',
      availableLanguage: ['English'],
    },
    {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'info@bluelockfreights.com',
      telephone: '+1-616-438-6969',
      areaServed: 'US',
      availableLanguage: ['English'],
    },
  ],
  identifier: [
    { '@type': 'PropertyValue', propertyID: 'USDOT', value: '4557330' },
    { '@type': 'PropertyValue', propertyID: 'MC', value: '1810883' },
  ],
  sameAs: [PORTAL],
};

// ---- JobPosting shared bits ----

const HIRING_ORG = {
  '@type': 'Organization',
  name: ORG_NAME,
  legalName: LEGAL_NAME,
  sameAs: WEBSITE,
};

const JOB_LOCATION = {
  '@type': 'Place',
  address: ADDRESS,
};

const APPLICANT_LOCATION_US = {
  '@type': 'Country',
  name: 'US',
};

const DATE_POSTED = '2026-02-12';
const VALID_THROUGH = '2026-12-31';
const INDUSTRY = 'Trucking and Freight';
const OCCUPATIONAL_CATEGORY = '53-3032 Heavy and Tractor-Trailer Truck Drivers';

const baseJobPosting = (extra) => ({
  '@context': 'https://schema.org',
  '@type': 'JobPosting',
  datePosted: DATE_POSTED,
  validThrough: VALID_THROUGH,
  industry: INDUSTRY,
  occupationalCategory: OCCUPATIONAL_CATEGORY,
  hiringOrganization: HIRING_ORG,
  jobLocation: JOB_LOCATION,
  applicantLocationRequirements: APPLICANT_LOCATION_US,
  directApply: false,
  ...extra,
});

// ---- Individual job postings ----

export const JOB_POSTING_LEASE_ON = baseJobPosting({
  identifier: {
    '@type': 'PropertyValue',
    name: 'BlueLock Freights Job ID',
    value: 'BL-LEASE-ON-2026',
  },
  title: 'CDL-A Lease-On Owner-Operator',
  description:
    '<p>Lease-On Owner-Operator opportunity with BlueLock Transport LLC (operating as BlueLock Freights). Drivers provide their own truck and lease onto our authority (USDOT 4557330 / MC 1810883).</p>' +
    '<p><strong>Compensation:</strong> 84% of linehaul revenue to the driver, 16% to BlueLock. Weekly settlements. Specific deductions (e.g., trailer use, insurance, escrow, plates, fuel programs) are outlined in the executed operating agreement.</p>' +
    '<p><strong>Requirements:</strong> Valid CDL-A, current DOT medical card, satisfactory MVR and PSP, minimum verifiable OTR experience as defined by recruiting, current truck registration and inspection, and proof of physical damage / occupational accident coverage as required.</p>' +
    '<p><strong>Important:</strong> No income is guaranteed. Compensation varies by experience and freight availability. All program terms, deductions, and obligations are defined in the executed operating agreement.</p>',
  employmentType: 'CONTRACTOR',
  jobBenefits:
    'Weekly settlements; coordinated dispatch under company authority; after-hours operations support.',
});

export const JOB_POSTING_DISPATCH_ONLY = baseJobPosting({
  identifier: {
    '@type': 'PropertyValue',
    name: 'BlueLock Freights Job ID',
    value: 'BL-DISPATCH-2026',
  },
  title: 'Owner-Operator (Own Authority) — Dispatch-Only Services',
  description:
    '<p>Dispatch-Only service for Owner-Operators who operate under their own USDOT / MC authority. BlueLock Freights coordinates load booking, broker communication, rate negotiation, paperwork, and back-office support.</p>' +
    '<p><strong>Fee Structure:</strong> 8% of gross revenue or $400 per week per truck, as defined in your dispatch agreement.</p>' +
    '<p><strong>Requirements:</strong> Active USDOT / MC authority, active operating authority status with FMCSA, current cargo and auto liability insurance with required limits, W-9, and signed dispatch agreement.</p>' +
    '<p><strong>Important:</strong> No revenue is guaranteed. Lane availability, broker terms, and seasonal market conditions affect freight access.</p>',
  employmentType: 'CONTRACTOR',
  jobBenefits:
    'Professional load coordination; broker setup and communication; rate confirmation and BOL handling; tracking/macropoint setup; accessorial documentation support.',
});

export const JOB_POSTING_COMPANY_DRIVER = baseJobPosting({
  identifier: {
    '@type': 'PropertyValue',
    name: 'BlueLock Freights Job ID',
    value: 'BL-COMPANY-DRIVER-2026',
  },
  title: 'CDL-A Company Driver',
  description:
    '<p>Company Driver position with BlueLock Transport LLC (operating as BlueLock Freights). Drive BlueLock-provided equipment under USDOT 4557330 / MC 1810883 authority. Dispatch, maintenance, fuel programs, and insurance are coordinated by BlueLock.</p>' +
    '<p><strong>Compensation:</strong> Pay structure (mileage, percentage, or hourly as applicable), home-time expectations, benefits, and accessorial pay are defined in the executed Company Driver Agreement.</p>' +
    '<p><strong>Requirements:</strong> Valid CDL-A, current DOT medical card, satisfactory MVR and PSP, minimum verifiable OTR experience as defined by recruiting, ability to pass DOT drug & alcohol pre-employment testing, and clean background screening.</p>' +
    '<p><strong>Important:</strong> No income is guaranteed. Additional compensation may be available for detention, layover, driver assist, multiple stops, TONU, and specialized services when documented and approved; accessorial pay is not guaranteed.</p>',
  employmentType: 'FULL_TIME',
  jobBenefits:
    'BlueLock-provided truck and trailer; coordinated dispatch; maintenance and fuel programs; cargo and auto liability insurance.',
});

export const JOB_POSTING_DRIVERS_GENERAL = baseJobPosting({
  identifier: {
    '@type': 'PropertyValue',
    name: 'BlueLock Freights Job ID',
    value: 'BL-DRIVER-2026',
  },
  title: 'CDL-A Driver — Owner-Operator, Lease-On, or Company Driver',
  description:
    '<p>BlueLock Freights (BlueLock Transport LLC) is recruiting professional drivers across three structured tracks. Apply once and our recruiting team will match you with the right program.</p>' +
    '<ul>' +
    '<li><strong>Lease-On Owner-Operator</strong> — 84% / 16% linehaul split, run under BlueLock authority (USDOT 4557330 / MC 1810883).</li>' +
    '<li><strong>Dispatch-Only</strong> — Run under your own authority; fee is 8% of gross revenue or $400/week per truck, per dispatch agreement.</li>' +
    '<li><strong>Company Driver</strong> — Drive BlueLock-provided equipment under BlueLock authority; pay per Company Driver Agreement.</li>' +
    '</ul>' +
    '<p><strong>Requirements (track-dependent):</strong> Valid CDL-A, current DOT medical card, satisfactory MVR and PSP, minimum verifiable OTR experience as defined by recruiting, and additional documentation as applicable to the chosen program.</p>' +
    '<p><strong>Important:</strong> No income is guaranteed. Compensation varies by program, experience, and freight availability. Accessorial pay (detention, layover, driver assist, multiple stops, TONU, specialized services) may be available when documented and approved; not guaranteed.</p>',
  employmentType: ['CONTRACTOR', 'FULL_TIME'],
  jobBenefits:
    'Weekly settlements; coordinated dispatch; after-hours operations support; structured written agreements.',
});

export default ORGANIZATION_SCHEMA;
